import { supabase } from "@/lib/supabaseClient";
import { logAndThrow } from "@/utils/errorUtils";
import {
  mapWriteScopeRows,
  partitionGradeWritableOfficials,
  partitionWritableOfficials,
  type OfficialIdentifierQuery,
  type OfficialWriteScope,
  type OfficialWriteScopeRow,
} from "@/lib/officials/sharedOfficials";

export interface OfficialMatch {
  officialId: string;
  firstName: string;
  lastName: string;
  firstNameAr: string | null;
  lastNameAr: string | null;
  category: string;
  matchKind: "faf_id" | "email" | "phone";
  leagueNames: string[];
  leagueNamesAr: string[];
  isMember: boolean;
}

export interface LinkOfficialResult {
  success: boolean;
  action?: "linked" | "reactivated" | "already_member";
  officialId?: string;
  error?: string;
}

/** PostgREST / Postgres « function does not exist ». */
function isMissingFunctionError(error: { code?: string } | null | undefined): boolean {
  return error?.code === "PGRST202" || error?.code === "42883";
}

/**
 * Write scope of the listed officials. `null` while `get_official_write_scope` is not
 * deployed (its migration ships after the webapp): no server guard yet, everything is
 * writable as before. Any other error throws.
 */
export async function getOfficialWriteScope(officialIds: string[]): Promise<Map<string, OfficialWriteScope> | null> {
  const ids = Array.from(new Set(officialIds.filter(Boolean)));
  if (ids.length === 0) return new Map();
  const { data, error } = await supabase.rpc("get_official_write_scope", { p_official_ids: ids });
  if (error) {
    if (isMissingFunctionError(error)) return null;
    logAndThrow(error, "Failed to read official write scope");
  }
  return mapWriteScopeRows(data as OfficialWriteScopeRow[]);
}

const allWritable = (ids: string[]) => ({ writable: ids, skipped: [] as string[] });

/**
 * Location (and address) of several officials. Shared officials the caller cannot
 * write are skipped instead of failing the whole UPDATE on the server guard.
 */
export async function bulkUpdateOfficialsLocation(
  officialIds: string[],
  locationId: string,
  options?: { address?: string | null }
): Promise<{ updated: string[]; skipped: string[] }> {
  if (!officialIds || officialIds.length === 0) return { updated: [], skipped: [] };
  const scope = await getOfficialWriteScope(officialIds);
  const { writable, skipped } = scope ? partitionWritableOfficials(officialIds, scope) : allWritable(officialIds);
  if (writable.length === 0) return { updated: [], skipped };
  const payload: Record<string, any> = { location_id: locationId };
  if (options && Object.prototype.hasOwnProperty.call(options, "address")) {
    payload.address = options.address ?? null;
  }
  const { data, error } = await supabase.from("officials").update(payload).in("id", writable).select("id");
  if (error) logAndThrow(error, "Failed to bulk update officials location");
  // Rows RLS let through; the others were silently not updated.
  const done = new Set(((data ?? []) as Array<{ id: string }>).map((r) => r.id));
  return {
    updated: writable.filter((id) => done.has(id)),
    skipped: [...skipped, ...writable.filter((id) => !done.has(id))],
  };
}

/**
 * Grade of several officials. The server raises OFFICIAL_GRADE_OUT_OF_SCOPE for the whole
 * batch as soon as one official belongs to no league of the caller: those are skipped.
 */
export async function bulkUpdateOfficialsCategory(
  officialIds: string[],
  category: string,
  userId: string | null
): Promise<{ updated: string[]; skipped: string[] }> {
  if (!officialIds || officialIds.length === 0) return { updated: [], skipped: [] };
  const scope = await getOfficialWriteScope(officialIds);
  const { writable, skipped } = scope ? partitionGradeWritableOfficials(officialIds, scope) : allWritable(officialIds);
  if (writable.length === 0) return { updated: [], skipped };
  const { error } = await supabase
    .from("officials")
    .update({ category, updated_by: userId })
    .in("id", writable);
  if (error) logAndThrow(error, "Failed to bulk update officials category");
  return { updated: writable, skipped };
}

export interface ArchiveOfficialsScope {
  /** `officials.is_archived` is global (every league): super-admin only. */
  superAdmin: boolean;
  /** League the caller works in; its membership is set inactive for a league user. */
  leagueId: string | null;
  userId: string | null;
  reason?: string | null;
}

export interface ArchiveOfficialsResult {
  /** global = officials.is_archived; league = league_officials.status 'inactive'. */
  scope: "global" | "league";
  updated: string[];
  /** League path: no active membership of the current league (or already inactive, or not writable). */
  skipped: string[];
}

/**
 * « Archiver » an official. Super-admin: the person, for every league. League user: the
 * membership of the current league only (`inactive` + deactivated_at / motif), other
 * leagues untouched (the server refuses officials.is_archived with
 * OFFICIAL_ARCHIVE_SUPER_ADMIN_ONLY). Memberships already inactive are left as they are.
 * A league user with no league selected gets OFFICIAL_ARCHIVE_NO_LEAGUE.
 */
export async function archiveOfficialsInScope(
  officialIds: string[],
  scope: ArchiveOfficialsScope
): Promise<ArchiveOfficialsResult> {
  const ids = Array.from(new Set((officialIds ?? []).filter(Boolean)));
  if (scope.superAdmin) {
    if (ids.length === 0) return { scope: "global", updated: [], skipped: [] };
    const { error } = await supabase
      .from("officials")
      .update({ is_archived: true, updated_by: scope.userId })
      .in("id", ids);
    if (error) logAndThrow(error, "Failed to archive officials");
    return { scope: "global", updated: ids, skipped: [] };
  }
  if (!scope.leagueId) {
    throw new Error("OFFICIAL_ARCHIVE_NO_LEAGUE");
  }
  if (ids.length === 0) return { scope: "league", updated: [], skipped: [] };
  const payload: Record<string, any> = {
    status: "inactive",
    deactivated_at: new Date().toISOString(),
  };
  if (scope.reason) payload.deactivation_reason = scope.reason;
  const { data, error } = await supabase
    .from("league_officials")
    .update(payload)
    .eq("league_id", scope.leagueId)
    .neq("status", "inactive")
    .in("official_id", ids)
    .select("official_id");
  if (error) logAndThrow(error, "Failed to deactivate league memberships");
  const done = new Set(((data ?? []) as Array<{ official_id: string }>).map((r) => r.official_id));
  return {
    scope: "league",
    updated: ids.filter((id) => done.has(id)),
    skipped: ids.filter((id) => !done.has(id)),
  };
}

export async function findOfficialMatches(leagueId: string, query: OfficialIdentifierQuery): Promise<OfficialMatch[]> {
  const { data, error } = await supabase.rpc("find_official_matches", {
    p_league_id: leagueId,
    p_faf_person_id: query.fafPersonId,
    p_email: query.email,
    p_phone: query.phone,
  });
  if (error) logAndThrow(error, "Failed to search officials by identifier");
  return ((data ?? []) as any[]).map((r) => ({
    officialId: r.official_id,
    firstName: r.first_name,
    lastName: r.last_name,
    firstNameAr: r.first_name_ar,
    lastNameAr: r.last_name_ar,
    category: r.category,
    matchKind: r.match_kind,
    leagueNames: r.league_names ?? [],
    leagueNamesAr: r.league_names_ar ?? [],
    isMember: !!r.is_member,
  }));
}

export async function linkOfficialToLeague(leagueId: string, officialId: string): Promise<LinkOfficialResult> {
  const { data, error } = await supabase.rpc("link_official_to_league", {
    p_league_id: leagueId,
    p_official_id: officialId,
  });
  if (error) logAndThrow(error, "Failed to link official to league");
  const r = (data ?? {}) as Record<string, any>;
  return { success: !!r.success, action: r.action, officialId: r.official_id, error: r.error };
}
